import React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ScrollArea } from '@/components/ui/scroll-area';
import { NAV_SECTIONS } from '@/config/navigation';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

export const SidebarNav = ({ onNavigate, withBranding = true, idPrefix = 'sidebar' }) => {
  const { isAdmin } = useAuth();
  const { t } = useTranslation();

  // Crew Leads only see items that are NOT admin/owner-only. Admins see
  // everything. We filter per-section and then drop any section whose items
  // were fully filtered out — so a Crew Lead never sees an empty header.
  const sectionsForRender = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) => (!item.adminOnly && !item.ownerOnly) || isAdmin
    ),
  })).filter((section) => section.items.length > 0);

  return (
    <div className="flex h-full flex-col bg-[hsl(var(--sidebar))]">
      {withBranding ? (
        <div
          className="px-5 pt-5 pb-4 border-b border-border flex flex-col items-center gap-3"
          data-testid="sidebar-brand-shield"
        >
          {/* Hero lockup — K.O. CO+RE crest over "PQ | Powered by
              INTELLIGENCE" (John, 2026-09-27). Rendered as TWO images so
              the PQ line can run the full sidebar width: as one image its
              size was locked to the shield's, and it read too small on a
              phone (John, 2026-09-29: "bigger"). */}
          <img
            src="/brand/ko-shield-hero.webp"
            alt="K.O. CO+RE Systems"
            className="h-28 w-auto object-contain select-none"
            draggable={false}
          />
          <img
            src="/brand/pq-powered-by.webp"
            alt="Powered by PQ Intelligence"
            className="w-full max-w-[230px] h-auto select-none"
            draggable={false}
          />
        </div>
      ) : null}

      <ScrollArea className="flex-1 kc-scroll">
        <nav
          className="px-3 pb-6 pt-4 space-y-4"
          data-testid={`${idPrefix}-nav`}
        >
          {sectionsForRender.map((section, sectionIndex) => (
            <div
              key={section.key}
              className="space-y-1"
              data-testid={`${idPrefix}-section-${section.key}`}
            >
              {/* Section header — same treatment as the legacy OPERATIONS
                  label. Hidden for sections whose `label` is null (Dashboard
                  group at the top). Top-of-list section gets no extra
                  spacing; subsequent sections get a small lead. */}
              {section.label ? (
                <div
                  className={cn(
                    'px-2',
                    sectionIndex === 0 ? 'pb-1' : 'pt-2 pb-1'
                  )}
                >
                  <span
                    className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground"
                    data-testid={`${idPrefix}-section-header-${section.key}`}
                  >
                    {section.i18nKey
                      ? t(section.i18nKey, { defaultValue: section.label })
                      : section.label}
                  </span>
                </div>
              ) : null}

              {section.items.map((item) => {
                const Icon = item.icon;
                const IconImage = item.iconImage;
                return (
                  <NavLink
                    key={item.key}
                    to={item.path}
                    end={item.end || false}
                    onClick={onNavigate}
                    data-testid={`${idPrefix}-nav-item-${item.key}`}
                    className={({ isActive }) =>
                      cn(
                        'group relative flex items-center gap-3 h-10 px-3 rounded-md text-sm font-medium tracking-[0.01em] transition-colors duration-150',
                        isActive
                          ? 'bg-[hsl(var(--sidebar-active))] text-foreground border border-[hsl(var(--primary))]/20'
                          : 'text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] border border-transparent'
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          aria-hidden
                          className={cn(
                            'absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[2px] rounded-full transition-colors duration-150',
                            isActive
                              ? 'bg-[hsl(var(--primary))]'
                              : 'bg-transparent group-hover:bg-[hsl(var(--primary))]/30'
                          )}
                        />
                        {IconImage ? (
                          <img
                            src={IconImage}
                            alt=""
                            aria-hidden="true"
                            className={cn(
                              'h-6 w-auto shrink-0 transition-opacity duration-150',
                              isActive ? 'opacity-100' : 'opacity-75 group-hover:opacity-100'
                            )}
                          />
                        ) : (
                          <Icon
                            size={18}
                            strokeWidth={2}
                            className={cn(
                              'shrink-0',
                              isActive
                                ? 'text-[hsl(var(--primary))]'
                                : 'text-muted-foreground group-hover:text-foreground'
                            )}
                          />
                        )}
                        {item.brandWordmark ? (
                          // Brand-marked nav entry (PQ Intelligence): the
                          // wordmark IS the label; alt keeps it accessible.
                          <img
                            src={item.brandWordmark}
                            alt={item.i18nKey ? t(item.i18nKey) : item.label}
                            className="h-3.5 w-auto select-none"
                            draggable={false}
                          />
                        ) : (
                          <span className="truncate">
                            {item.i18nKey ? t(item.i18nKey) : item.label}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
      </ScrollArea>

      <div className="border-t border-border px-4 py-3 flex flex-col gap-2">
        <div
          className="flex items-center gap-2.5"
          data-testid="sidebar-powered-by-pq"
        >
          <img
            src="/pq-intelligence-logo.png"
            alt=""
            aria-hidden="true"
            className="h-11 w-auto shrink-0"
            data-testid="sidebar-pq-shield"
          />
          <div className="flex flex-col leading-tight min-w-0 gap-1">
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80 truncate">
              {t('brand.powered_by', { defaultValue: 'Powered by' })}
            </span>
            <img
              src="/brand/pq-wordmark.webp"
              alt="PQ Intelligence"
              className="h-7 w-auto max-w-full self-start select-none"
              draggable={false}
            />
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          v0.1 · Foundation
        </div>
      </div>
    </div>
  );
};

export default SidebarNav;
